/**
 * Pure aggregation logic shared by every repository implementation.
 * For very large portfolios move this into a Postgres RPC / materialized view —
 * the DashboardSummary contract stays the same.
 */
import type { AuditLog, TenderSummary } from '@/types/domain';
import type { DashboardSummary, PendingDocumentRow } from '../repository';
import { TENDER_STATUS_ORDER } from '@/lib/status';
import { addDays, monthKey, monthRange, todayISO } from '@/lib/dates';
import { BUDGET_PAID_THRESHOLD, ORDER_TYPES, isBudgetOpen } from '@/lib/orderBudget';
import { FLOATED_BY_OPTIONS, TENDER_CATEGORIES, TENDERING_METHODS, isClassificationMissing } from '@/lib/tenderClassification';

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);

export function computeDashboard(
  all: TenderSummary[],
  pendingDocuments: PendingDocumentRow[],
  recentActivity: AuditLog[],
  today: string = todayISO(),
): DashboardSummary {
  const live = all.filter((t) => t.status !== 'draft' && t.status !== 'cancelled');

  const statusDistribution = TENDER_STATUS_ORDER.map((status) => ({
    status,
    count: all.filter((t) => t.status === status).length,
  }));

  const months = monthRange(monthKey(addDays(today, -330)), 12);
  const valueByMonth = months.map((m) => {
    const ts = live.filter((t) => t.awardDate && monthKey(t.awardDate) === m);
    return { month: m, value: sum(ts.map((t) => t.contractValue)), count: ts.length };
  });

  const inForce = all.filter((t) => ['active', 'expiring_soon', 'expired'].includes(t.status));
  const bands: [string, number, number][] = [
    ['0–25%', 0, 25], ['25–50%', 25, 50], ['50–75%', 50, 75], ['75–90%', 75, 90], ['90%+', 90, Infinity],
  ];
  const utilizationBands = bands.map(([band, lo, hi]) => ({
    band,
    count: inForce.filter((t) => t.utilizationPct >= lo && t.utilizationPct < hi).length,
  }));

  const byVendor = new Map<string, { vendorId: string; vendorName: string; count: number; value: number }>();
  live.forEach((t) => {
    if (!t.vendorId) return;
    const e = byVendor.get(t.vendorId) ?? { vendorId: t.vendorId, vendorName: t.vendorName ?? '—', count: 0, value: 0 };
    e.count += 1;
    e.value += t.contractValue;
    byVendor.set(t.vendorId, e);
  });

  const expiriesByMonth = monthRange(monthKey(today), 6).map((m) => {
    const ts = all.filter(
      (t) => (t.status === 'active' || t.status === 'expiring_soon') && t.endDate && monthKey(t.endDate) === m,
    );
    return { month: m, count: ts.length, value: sum(ts.map((t) => t.contractValue)) };
  });

  // Classification of every tender except cancelled ones (drafts included: they are being tendered).
  const classified = all.filter((t) => t.status !== 'cancelled');
  function breakdown<K extends string>(keys: K[], get: (t: TenderSummary) => K | null | undefined) {
    const rows = [...keys, 'missing' as const].map((key) => {
      const ts = classified.filter((t) => (get(t) ?? 'missing') === key);
      return { key, count: ts.length, value: sum(ts.map((t) => t.contractValue)) };
    });
    return rows.filter((r) => r.key !== 'missing' || r.count > 0);
  }
  const classification = {
    byMethod: breakdown(TENDERING_METHODS, (t) => t.tenderingMethod),
    byCategory: breakdown(TENDER_CATEGORIES, (t) => t.tenderCategory),
    byFloatedBy: breakdown(FLOATED_BY_OPTIONS, (t) => t.floatedBy),
    detailsMissing: all.filter((t) => isClassificationMissing(t)).length,
    byOrderType: ORDER_TYPES.map((key) => {
      const ts = classified.filter((t) => (t.orderType ?? 'original') === key);
      return { key, count: ts.length, value: sum(ts.map((t) => t.contractValue)) };
    }),
    byApprover: [] as { key: string; label: string; count: number; value: number }[],
  };

  // By approving authority (Approving Committee), largest count first; 'missing' = not entered.
  const approverRows = new Map<string, { key: string; label: string; count: number; value: number }>();
  classified.forEach((t) => {
    const key = t.approvingAuthorityId ?? 'missing';
    const e = approverRows.get(key) ?? { key, label: t.approvingAuthorityName ?? 'Not entered', count: 0, value: 0 };
    e.count += 1;
    e.value += t.contractValue;
    approverRows.set(key, e);
  });
  classification.byApprover = [...approverRows.values()].sort((a, b) => Number(a.key === 'missing') - Number(b.key === 'missing') || b.count - a.count);

  const budgetOpen = all.filter((t) => t.status !== 'cancelled' && isBudgetOpen(t.budgetStatus));
  const additionalBudget = {
    count: budgetOpen.length,
    needed: budgetOpen.filter((t) => t.budgetStatus === 'needed').length,
    requested: budgetOpen.filter((t) => t.budgetStatus === 'requested').length,
    amountSought: sum(budgetOpen.map((t) => t.budgetAmountSought ?? 0)),
    top: [...budgetOpen].sort((a, b) => (b.budgetAmountSought ?? 0) - (a.budgetAmountSought ?? 0)).slice(0, 8),
    // Running, untagged contracts with paid ≥ 90% of value (the profile also checks the quantity pace).
    suggested: all.filter((t) => (t.status === 'active' || t.status === 'expiring_soon') && (t.budgetStatus ?? 'none') === 'none'
      && t.contractValue > 0 && t.paidAmount >= BUDGET_PAID_THRESHOLD * t.contractValue).length,
  };

  return {
    classification,
    additionalBudget,
    totals: {
      tenders: all.length,
      active: all.filter((t) => t.status === 'active' || t.status === 'expiring_soon').length,
      expiringSoon: all.filter((t) => t.status === 'expiring_soon').length,
      expired: all.filter((t) => t.status === 'expired').length,
      contractValue: sum(live.map((t) => t.contractValue)),
      quantity: sum(live.map((t) => t.totalQuantity)),
      consumed: sum(live.map((t) => t.consumedQuantity)),
      remaining: sum(live.map((t) => t.remainingQuantity)),
    },
    statusDistribution,
    valueByMonth,
    utilizationBands,
    vendorContractCounts: [...byVendor.values()].sort((a, b) => b.count - a.count || b.value - a.value),
    expiriesByMonth,
    upcomingExpiries: all
      .filter((t) => t.status === 'expiring_soon')
      .sort((a, b) => (a.daysRemaining ?? 0) - (b.daysRemaining ?? 0)),
    recentActivity,
    pendingDocuments,
  };
}
